import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';
import type { Appointment } from '../types';

interface MonthViewProps {
  currentDate: Date;
  selectedDate: Date | null;
  appointments: Appointment[];
  onDateSelect: (date: Date) => void;
  onMonthChange: (direction: 'prev' | 'next') => void;
  onTodayClick: () => void;
}

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTH_KEYS = [
  'months.january', 'months.february', 'months.march', 'months.april',
  'months.may', 'months.june', 'months.july', 'months.august',
  'months.september', 'months.october', 'months.november', 'months.december'
];

// Helper function to format date as YYYY-MM-DD using local timezone
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export function MonthView({
  currentDate,
  selectedDate,
  appointments,
  onDateSelect,
  onMonthChange,
  onTodayClick,
}: MonthViewProps) {
  const { t } = useTranslation('calendar');

  // Generate calendar days for the current month
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);

    // Get the day of week for the first day (0 = Sunday, adjust to Monday = 0)
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6; // Sunday

    const days: (Date | null)[] = [];

    // Add empty cells for days before the first day
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  }, [currentDate]);

  // Create a map of dates that have appointments
  const appointmentDates = useMemo(() => {
    const dates = new Set<string>();
    appointments.forEach((apt) => {
      dates.add(apt.appointment_date);
    });
    return dates;
  }, [appointments]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isToday = (date: Date) => {
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) => {
    return selectedDate?.toDateString() === date.toDateString();
  };

  const hasAppointments = (date: Date) => {
    const dateStr = formatLocalDate(date);
    return appointmentDates.has(dateStr);
  };

  return (
    <div className="glass-effect rounded-[15px] p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => onMonthChange('prev')}
          className="w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>

        <div className="text-center">
          <h2 className="text-lg font-semibold text-white">
            {t(MONTH_KEYS[currentDate.getMonth()])} {currentDate.getFullYear()}
          </h2>
          <button
            onClick={onTodayClick}
            className="text-xs text-pp-orange hover:text-pp-orange/80"
          >
            {t('today')}
          </button>
        </div>

        <button
          onClick={() => onMonthChange('next')}
          className="w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 mb-2">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-white/60 py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((date, index) => (
          <div key={index} className="aspect-square">
            {date ? (
              <button
                onClick={() => onDateSelect(date)}
                className={cn(
                  'w-full h-full rounded-lg flex flex-col items-center justify-center relative transition-colors',
                  isToday(date) && !isSelected(date) && 'bg-pp-orange/20',
                  isSelected(date) && 'bg-pp-orange text-white',
                  !isSelected(date) && 'hover:bg-white/10'
                )}
              >
                <span className={cn(
                  'text-sm font-medium',
                  isToday(date) && !isSelected(date) && 'text-pp-orange',
                  !isSelected(date) && !isToday(date) && 'text-white'
                )}>
                  {date.getDate()}
                </span>

                {/* Appointment indicator */}
                {hasAppointments(date) && (
                  <span
                    className={cn(
                      'absolute bottom-1 w-1.5 h-1.5 rounded-full',
                      isSelected(date) ? 'bg-white' : 'bg-pp-orange'
                    )}
                  />
                )}
              </button>
            ) : (
              <div className="w-full h-full" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
